import React from 'react';
import { Helmet } from 'react-helmet-async';
import { appConfig } from '../../config/appConfig';
import PropTypes from 'prop-types';

const SEOHead = ({ 
  title, 
  description, 
  keywords = [], 
  canonical, 
  ogImage = appConfig.assets.logoBlack,
  ogType = 'website',
  structuredData = null,
  noindex = false,
  breadcrumbs = []
}) => {
  const siteUrl = appConfig.site.baseUrl;
  const fullTitle = title ? `${title}${appConfig.seo.titleSuffix}` : 'AMIVA+ | Business Valuation & M&A Advisory Services';
  const fullUrl = canonical ? `${siteUrl}${canonical}` : siteUrl;
  
  // Default keywords for all pages
  const defaultKeywords = appConfig.seo.defaultKeywords;
  
  const allKeywords = [...new Set([...defaultKeywords, ...keywords])].join(', ');

  // Organization structured data (appears on all pages)
  const organizationData = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${siteUrl}/#organization`,
    "name": appConfig.site.organizationName,
    "alternateName": appConfig.site.brandName,
    "url": siteUrl,
    "logo": {
      "@type": "ImageObject",
      "url": appConfig.assets.logoBlack,
      "width": 512,
      "height": 512
    },
    "description": "Boutique M&A advisory firm specializing in rapid business exits for companies with $5M-$50M revenue",
    "email": appConfig.site.contactEmail,
    "telephone": appConfig.site.contactPhone,
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "US"
    },
    "sameAs": appConfig.site.socialLinks,
    "priceRange": "$$$$",
    "areaServed": ["US", "CA", "GB", "AU"],
    "serviceType": ["Business Valuation", "M&A Advisory", "Exit Planning", "Buyer Sourcing"],
    "founder": {
      "@type": "Person",
      "name": "Michael A Pantouvakis",
      "jobTitle": "Founder & Managing Principal",
      "url": appConfig.links.linkedinProfile
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "reviewCount": "47",
      "bestRating": "5",
      "worstRating": "1"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "M&A Advisory Services",
      "itemListElement": [
        {
          "@type": "Service",
          "name": "Business Valuation",
          "description": "Free confidential business valuation using AI-powered analysis",
          "url": `${siteUrl}/services/business-valuation`
        },
        {
          "@type": "Service",
          "name": "Buyer Sourcing",
          "description": "Connect with 200,000 qualified buyers for your business",
          "url": `${siteUrl}/services/buyer-sourcing`
        }
      ]
    }
  };

  // Breadcrumb structured data
  const breadcrumbData = breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": `${siteUrl}${crumb.path}`
    }))
  } : null;

  // Combine all structured data
  const allStructuredData = [
    organizationData,
    breadcrumbData,
    structuredData
  ].filter(Boolean);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={allKeywords} />
      <meta name="author" content="AMIVA Equity Partners" />
      <link rel="canonical" href={fullUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={appConfig.seo.openGraph.siteName} />
      <meta property="og:locale" content={appConfig.seo.openGraph.locale} />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={ogImage} />
      <meta property="twitter:creator" content={appConfig.seo.openGraph.twitterCreator} />
      <meta property="twitter:site" content={appConfig.seo.openGraph.twitterSite} />
      
      {/* Additional Meta Tags */}
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      
      {/* Structured Data */}
      {allStructuredData.map((data, index) => (
        <script 
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
      ))}
      
      {/* Preconnect to external domains for performance */}
      {appConfig.seo.preconnect.map((url) => (
        <link key={url} rel="preconnect" href={url} />
      ))}
      {appConfig.seo.preconnect.map((url) => (
        <link key={`${url}-dns`} rel="dns-prefetch" href={url} />
      ))}
      
      {/* Alternate language versions if applicable */}
      <link rel="alternate" hrefLang="en-US" href={fullUrl} />
      <link rel="alternate" hrefLang="x-default" href={fullUrl} />
    </Helmet>
  );
};

export default SEOHead;

SEOHead.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string.isRequired,
  keywords: PropTypes.arrayOf(PropTypes.string),
  canonical: PropTypes.string,
  ogImage: PropTypes.string,
  ogType: PropTypes.string,
  structuredData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  noindex: PropTypes.bool,
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
  })),
};
